import { CheckCircleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { ITEM_META, META_SEPARATOR } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { WorkflowTemplate } from '@/services/data/CampaignService';

interface WorkflowLibraryCardPreviewProps {
  template: WorkflowTemplate;
  /** Optional "Yours" badge in title row. */
  ownerBadge?: boolean;
  /** Enables the production card's named-group hover treatment. */
  interactive?: boolean;
}

/**
 * Mirrors WorkflowLibraryCard (rendered in the Workflow Library dialog) —
 * Claude-Artifacts paper-on-table archetype (frame + offset paper rectangle
 * holding a cropped preview of the description + numbered step list with
 * fade-mask, and unboxed title + meta below the frame).
 *
 * Used in the editor's right pane so the user sees EXACTLY what the saved
 * workflow will look like in the library grid. Proof, not echo.
 */
export function WorkflowLibraryCardPreview({
  template,
  ownerBadge,
  interactive,
}: WorkflowLibraryCardPreviewProps): React.JSX.Element {
  const validSteps = (template.steps ?? []).filter((s) => s.action.trim().length > 0);
  const stepCount = validSteps.length;
  const reviewCount = validSteps.filter((s) => s.requires_approval).length;
  const estimate = (() => {
    const min = template.estimate_minutes_min;
    const max = template.estimate_minutes_max;
    if (min == null && max == null) return null;
    if (min != null && max != null) return min === max ? `${min} min` : `${min}–${max} min`;
    return `${min ?? max} min`;
  })();

  const hasContent = stepCount > 0 || (template.description ?? '').trim().length > 0;

  return (
    <div className="group">
      {/* Frame — same dimensions as WorkflowLibraryCard */}
      <div
        className={cn(
          'relative h-[180px] rounded-xl overflow-hidden transition-colors',
          'bg-muted/40 dark:bg-card',
          interactive &&
            'group-hover/workflow-card:bg-muted/60 dark:group-hover/workflow-card:bg-muted/50',
        )}
      >
        {/* Paper preview — anchored top, cropped at bottom with fade mask */}
        <div
          className={cn(
            'absolute inset-x-10 top-8 bottom-0 bg-card dark:bg-foreground/[0.08] border border-border/60 dark:border-foreground/10 rounded-t-md shadow-elevation-1 overflow-hidden',
            interactive &&
              'transition-shadow duration-200 ease-magnetic group-hover/workflow-card:shadow-elevation-2',
          )}
        >
          <div className="absolute inset-x-4 top-3 bottom-0 [mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]">
            {!hasContent && <p className={cn(ITEM_META)}>Add steps to see your card.</p>}
            {template.description && (
              <p className="text-detail leading-[1.7] text-foreground/65 mb-2.5 line-clamp-2">
                {template.description}
              </p>
            )}
            <ol className="space-y-1.5 text-detail leading-[1.7] text-foreground/70">
              {validSteps.map((step, idx) => (
                <li key={step.step_id ?? idx} className="flex items-baseline gap-2">
                  <span className="text-muted-foreground/40 tabular-nums shrink-0">{idx + 1}.</span>
                  <span className="flex-1">
                    {step.action}
                    {step.requires_approval && (
                      <CheckCircleIcon
                        className="ml-1 inline-block size-3.5 text-skill/70 align-middle"
                        weight="duotone"
                        aria-label="Requires your review"
                      />
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Unboxed footer — title + meta flat on background, BELOW the frame */}
      <div className="mt-3 px-0.5">
        <p className="text-caption font-medium text-foreground truncate flex items-center gap-2">
          {template.name || 'Untitled workflow'}
          {ownerBadge && (
            <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-1.5 py-0.5 text-micro font-medium uppercase tracking-wider text-muted-foreground/80">
              Yours
            </span>
          )}
        </p>
        {stepCount > 0 ? (
          <div className={cn('flex items-center gap-1.5 mt-1 flex-wrap', ITEM_META)}>
            <span>
              {stepCount} {stepCount === 1 ? 'step' : 'steps'}
            </span>
            {estimate && (
              <>
                <span className={META_SEPARATOR}>·</span>
                <span>{estimate}</span>
              </>
            )}
            {reviewCount > 0 && (
              <>
                <span className={META_SEPARATOR}>·</span>
                <span>
                  {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                </span>
              </>
            )}
          </div>
        ) : (
          <div className={cn('mt-1', ITEM_META)}>No steps yet</div>
        )}
      </div>
    </div>
  );
}
